<template>
  <b-modal
    v-model="show"
    id="announcement-post-modal"
    :title="$t('dashboard-announcements.title-post')"
    size="lg"
    centered
    header-bg-variant="primary"
    header-text-variant="white"
    @hidden="resetForm"
  >
    <b-form @submit.prevent="submitAnnouncement" class="px-3 py-2">
      <b-form-group
        label-for="announcement-title"
        label-class="font-weight-bold h5"
        class="mb-4"
      >
        <template #label>
          {{ $t('dashboard-announcements.label-title') }} <span class="text-danger">*</span>
        </template>
        <b-form-input
          id="announcement-title"
          v-model="form.title"
          required
          size="lg"
          :placeholder="$t('dashboard-announcements.placeholder-title')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        label-for="announcement-body"
        label-class="font-weight-bold h5"
        class="mb-4"
      >
        <template #label>
          {{ $t('dashboard-announcements.label-body') }} <span class="text-danger">*</span>
        </template>
        <b-form-textarea
          id="announcement-body"
          v-model="form.body"
          required
          rows="8"
          size="lg"
          :placeholder="$t('dashboard-announcements.placeholder-body')"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group
        :label="$t('dashboard-announcements.label-attachment') + ' ' + $t('dashboard-announcements.label-attachment-hint')"
        label-for="announcement-attachment"
        label-class="font-weight-bold h6 text-muted"
      >
        <b-form-file
          id="announcement-attachment"
          v-model="form.file"
          size="lg"
          accept=".pdf,.jpg,.jpeg,.png"
          :placeholder="$t('dashboard-announcements.placeholder-file')"
          :drop-placeholder="$t('dashboard-announcements.placeholder-drop')"
          :browse-text="$t('common.word-upload')"
        ></b-form-file>
        <small v-if="fileError" class="text-danger">{{ fileError }}</small>
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button variant="outline-secondary" size="lg" class="mr-2" @click="show = false" :disabled="submitting">
          {{ $t('common.word-close') }}
        </b-button>
        <b-button variant="primary" size="lg" @click="submitAnnouncement" :disabled="submitting || !isValid">
          <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
          {{ $t('dashboard-announcements.button-post') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import imageCompression from "browser-image-compression";
import announcementApi from "@/api/announcements";

export default {
  name: "AnnouncementPostModal",
  data() {
    return {
      show: false,
      submitting: false,
      form: {
        title: "",
        body: "",
        file: null
      }
    };
  },
  computed: {
    fileError() {
      if (!this.form.file) return null;
      const file = this.form.file;
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        return this.$t('dashboard-announcements.error-file-type');
      }
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
      const maxMB = isImage ? 2 : 20;
      if (file.size > maxMB * 1024 * 1024) {
        return this.$t('dashboard-announcements.error-file-size', { max: maxMB });
      }
      return null;
    },
    isValid() {
      return this.form.title.trim().length > 0 && this.form.body.trim().length > 0 && !this.fileError;
    }
  },
  methods: {
    open() {
      this.show = true;
    },
    resetForm() {
      this.form.title = "";
      this.form.body = "";
      this.form.file = null;
    },
    async submitAnnouncement() {
      if (!this.isValid) return;

      try {
        this.submitting = true;

        let attachmentGcsPath = null;

        if (this.form.file) {
          let attachment = this.form.file;

          // 画像（JPG/PNG）の場合は圧縮処理を行う
          if (attachment.type === 'image/jpeg' || attachment.type === 'image/png') {
            const options = {
              maxSizeMB: 2,              // 最大2MB（お知らせ用なので少し余裕を持たせる）
              maxWidthOrHeight: 1920,    // 最大幅1920px（明瞭さを保つため）
              useWebWorker: false        // CSP制限を考慮
            };

            try {
              attachment = await imageCompression(attachment, options);
              // 圧縮後のファイル名を維持（Blobになるため）
              attachment = new File([attachment], this.form.file.name, { type: attachment.type });
            } catch (error) {
              console.error('Image compression failed, using original file:', error);
            }
          }

          // 1. 署名付きアップロードURLを発行
          const { data: urlInfo } = await announcementApi.issueUploadUrl({
            filename: attachment.name,
            mimeType: attachment.type,
            size: attachment.size
          });

          // 2. GCSへ直接PUT（Cloud Armor配下を通らない）
          await announcementApi.uploadToGcs(urlInfo.upload_url, attachment);

          attachmentGcsPath = urlInfo.attachment_gcs_path;
        }

        // 3. JSONでお知らせを登録
        await announcementApi.postAnnouncement({
          title: this.form.title,
          body: this.form.body,
          attachmentGcsPath
        });

        this.$emit("posted");
        this.show = false;

        // シンプルな完了通知
        this.$toast.success(this.$t('dashboard-announcements.message-posted'));
      } catch (error) {
        console.error("Failed to post announcement:", error);
        this.$toast.error(this.$t('dashboard-announcements.message-post-failed'));
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
